import { SsoConnectionTooLarge, SsoConnectionUser } from '@choco-admin/types';

import { useSsoPageConnectionUsersQuery } from '../../../generated';
import { mapToSsoConnectionUsers } from '../utils';

export interface UseSsoConnectionUsersInput {
  connectionId: string;
}

export interface UseSsoConnectionUsersResult {
  users: SsoConnectionUser[];
  /** Set when the backend declined to return a list rather than truncate it. */
  tooLarge: SsoConnectionTooLarge | null;
  notFound: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useSsoConnectionUsers = ({
  connectionId,
}: UseSsoConnectionUsersInput): UseSsoConnectionUsersResult => {
  const { data, loading, error, refetch } = useSsoPageConnectionUsersQuery({
    variables: { input: { connectionId } },
    fetchPolicy: 'cache-and-network',
    skip: !connectionId,
  });

  const result = data?.ssoConnectionUsers;

  const authorizationError =
    result?.__typename === 'AuthorizationFailure'
      ? new Error(result.message ?? 'Not authorized to view SSO users.')
      : null;

  return {
    users:
      result?.__typename === 'SsoConnectionUserList'
        ? mapToSsoConnectionUsers(result.users)
        : [],
    tooLarge:
      result?.__typename === 'SsoConnectionTooLarge'
        ? {
            limit: result.limit,
            message:
              result.message ??
              'This connection has too many users to list without pagination.',
          }
        : null,
    notFound: result?.__typename === 'NotFoundError',
    isLoading: loading,
    error: error ?? authorizationError,
    refetch: () => {
      refetch();
    },
  };
};
